import { Navigate, Outlet, redirect, useRouteError } from 'react-router-dom';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v7';
import { Suspense } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AppLayout from '@/components/common/AppLayout';
import PasswordResetGuard from '@/components/common/PasswordResetGuard';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import { SiteGroupUrlSync } from '@/components/common/SiteGroupUrlSync';

const lazyPage = (importer: () => Promise<{ default: React.ComponentType }>) =>
  async () => {
    // Do NOT auto-reload on chunk miss. Mid-SPA-transition reload/assign either
    // bounces to the previous route or loops forever. Let errorElement's Refresh
    // button do an intentional full document load instead.
    const { default: Component } = await importer();
    return { Component };
  };

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
      <CircularProgress size={36} sx={{ color: 'primary.main' }} />
    </Box>
  );
}

function RouteErrorPage() {
  const err = useRouteError();
  const isChunkError =
    err instanceof Error &&
    (err.message.includes('Failed to fetch dynamically imported module') ||
      err.message.includes('Importing a module script failed'));

  return (
    <Box
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '60vh', gap: 2, p: 3,
      }}
    >
      <Typography variant="h6">
        {isChunkError ? 'A new version is available.' : 'Something went wrong.'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        {isChunkError
          ? 'The page could not load because a new version of the app was deployed. Refreshing will fix it.'
          : 'Please try refreshing the page. If the problem persists, contact support.'}
      </Typography>
      <Button variant="contained" onClick={() => window.location.reload()}>
        Refresh
      </Button>
    </Box>
  );
}

function RootLayout() {
  // SiteGroupUrlSync: Chrome omnibox prerender / Self-Reg SiteGroup timing — see component.
  return (
    <>
      <SiteGroupUrlSync />
      <Outlet />
    </>
  );
}

export const router = createBrowserRouter([
  {
    // Root wrapper — errorElement here is the catch-all for any unhandled route error,
    // including lazy chunk-load failures (Refresh forces a full document reload).
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    // Every route is lazy, so the first render happens before the router has resolved
    // a match. Without this the router warns and renders nothing during that window.
    hydrateFallbackElement: <PageLoader />,
    children: [
      // Public: portal selection home (pick Patient / Referring / Admin / UM)
      { path: '/', lazy: lazyPage(() => import('@/pages/PortalSelection')) },

      // Public: facility picker (runs after Patient Portal is chosen)
      { path: '/facility-selection', lazy: lazyPage(() => import('@/pages/FacilitySelection')) },

      // Public: landing hub (welcome + options) — shown after a facility is selected
      { path: '/home', lazy: lazyPage(() => import('@/pages/Home')) },

      // Public: SR training material aliases (RPP-288 / RPP-401)
      { path: 'TrainingMaterial/:surface', lazy: lazyPage(() => import('@/pages/TrainingMaterial')) },

      // Public: username/password sign-in form
      { path: '/sign-in', lazy: lazyPage(() => import('@/pages/SignIn')) },

      // Public: get help / technical assistance form
      { path: '/get-help', lazy: lazyPage(() => import('@/pages/GetHelp')) },

      // Public: iCode entry form (lookup happens here; ICodeVerify renders the identity challenge)
      { path: '/icode-entry', lazy: lazyPage(() => import('@/pages/ICodeEntry')) },

      // Public: account recovery
      { path: '/account-recovery', lazy: lazyPage(() => import('@/pages/AccountRecovery')) },

      // Public: stand-alone create-account page (same AccountCreationWizard that Home.tsx
      // opens as a dialog). Entered from iCode Type 'C' success with { patientKey, skipTo }
      // in router state so the user lands directly on CreatePasswordStep.
      { path: '/create-account', lazy: lazyPage(() => import('@/pages/CreateAccount')) },

      // Public: guest schedule flow — identity verification then inline SchedulingWizard.
      // Also handles the legacy /appointments/guest deep-link from Home.tsx.
      { path: '/guest-schedule', lazy: lazyPage(() => import('@/pages/GuestSchedule')) },
      { path: '/appointments/guest', lazy: lazyPage(() => import('@/pages/GuestSchedule')) },

      // Public: Parking Lot Mobile Mammo clinic-day entry (RPP-502). GUID-only.
      { path: '/mobile/:guid', lazy: lazyPage(() => import('@/pages/MobileClinicDay')) },
      { path: '/Mobile/:guid', lazy: lazyPage(() => import('@/pages/MobileClinicDay')) },

      // Public: Self-Reg unauthenticated scheduling (RPP-720)
      { path: '/self-reg', lazy: lazyPage(() => import('@/pages/SelfRegSchedule')) },

      // Public: phone-side attachment upload session opened from a QR code in scheduling.
      { path: '/schedule-attachment-upload/:token', lazy: lazyPage(() => import('@/pages/MobileAttachmentUpload')) },

      // Public: shared exam access
      { path: '/share/:guid', lazy: lazyPage(() => import('@/pages/ShareAccess')) },
      { path: '/ExamShareAccess/:guid', lazy: lazyPage(() => import('@/pages/ShareAccess')) },
      { path: '/home/ExamShareAccess/:guid', lazy: lazyPage(() => import('@/pages/ShareAccess')) },

      // Public: authorized medical-record access invite landing
      { path: '/accept-invite', lazy: lazyPage(() => import('@/pages/AcceptInvite')) },

      // Legacy redirect: /AuthorizedAccess/?SiteGroup=Advanced&id=DV6DHV58
      //   → /accept-invite?code=DV6DHV58&siteGroup=Advanced
      {
        path: '/AuthorizedAccess',
        loader: ({ request }) => {
          const src  = new URL(request.url);
          const dest = new URLSearchParams();
          const code      = src.searchParams.get('id');
          const siteGroup = src.searchParams.get('SiteGroup');
          if (code)      dest.set('code', code);
          if (siteGroup) dest.set('siteGroup', siteGroup);
          return redirect(`/accept-invite?${dest}`);
        },
      },

      // Public: admin remote access login — admin enters l_user credentials to view as a patient
      { path: '/remote-access-login', lazy: lazyPage(() => import('@/pages/RemoteAccessLogin')) },

      // Public: anonymous Good Faith Estimate request — Pathway B on-demand cost estimate
      // for patients who don't yet have a portal account. No auth required.
      { path: '/gfe-request', lazy: lazyPage(() => import('@/pages/GFERequest')) },

      // TEMP: unauthenticated Figma proto landing page for demos. No auth, no layout.
      { path: '/demo', lazy: lazyPage(() => import('@/pages/DemoLanding')) },

      // Public: patient engagement landing page — single-link experience reachable
      // from an SMS/email. DOB gate then a guided task hub for one upcoming
      // appointment. Token is validated mock-side until /api/Engage/* exists.
      { path: '/engage/:token', lazy: lazyPage(() => import('@/pages/Engage')) },

      // Public: iCode verify landing page. iCode is passed via router state (not URL) so it
      // doesn't appear in the address bar. Deep-linking to /icode with no state redirects to /home.
      { path: '/icode', lazy: lazyPage(() => import('@/pages/ICodeVerify')) },

      // Protected: app
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <PasswordResetGuard />,
            children: [
              {
                element: <Suspense fallback={<PageLoader />}><AppLayout /></Suspense>,
                children: [
                  { index: true, element: <Navigate to="/dashboard" replace /> },
                  { path: 'dashboard',       lazy: lazyPage(() => import('@/pages/Dashboard')) },
                  { path: 'dashboard-ai',    lazy: lazyPage(() => import('@/pages/DashboardAI')) },
                  { path: 'exams',           lazy: lazyPage(() => import('@/pages/Exams')) },
                  { path: 'appointments',    lazy: lazyPage(() => import('@/pages/Appointments')) },
                  { path: 'health-summary',  lazy: lazyPage(() => import('@/pages/HealthSummary')) },
                  { path: 'messages',        lazy: lazyPage(() => import('@/pages/Messages')) },
                  { path: 'share',           lazy: lazyPage(() => import('@/pages/Share')) },
                  { path: 'help',            lazy: lazyPage(() => import('@/pages/Help')) },
                  { path: 'account',         lazy: lazyPage(() => import('@/pages/Account')) },
                  { path: 'gfe',             lazy: lazyPage(() => import('@/pages/GFE')) },
                  { path: 'schedule',        lazy: lazyPage(() => import('@/pages/Schedule')) },
                ],
              },
            ],
          },
        ],
      },

      // Catch-all
      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);
